import type { Chart } from 'klinecharts'
import { CHART_TYPES, TIMEFRAME_OPTIONS, indicatorByName } from './catalog'
import type { StagedChart } from '@/bot/chartPush'
import { isTradeMark, stripTransientMarkKeys } from './tradeIdeas'
import type {
  ChartSettings,
  ChartTypeId,
  IndicatorInstance,
  IndicatorSettings,
  VolumeState,
  WorkbenchNote,
} from './types'

/**
 * The persisted workbench state (v2) — a per-symbol map of chart environments
 * (market view, indicators, drawings, notes, AI-push bookkeeping) plus the
 * shared appearance settings. Saved per bot via /ai/bot/workbench/save; the
 * client owns and versions this schema (the server stores it as an opaque
 * blob). v1 blobs (one flat symbol per bot) are migrated on parse.
 *
 * Multi-tab caveat: saves are last-write-wins on the WHOLE blob, so a stale
 * tab can clobber slices for symbols it never touched — same class of
 * limitation the single-symbol v1 blob had.
 */

export interface SavedDrawing {
  /** klinecharts overlay name (segment, rectangle, fibRetracement, buyEntry, …). */
  name: string
  /** ms timestamps; dataIndex is dropped — the engine re-resolves it from data. */
  points: Array<{ timestamp?: number; value?: number }>
  /** Filtered style subtrees, mirroring what the floating toolbar writes. */
  styles?: { line?: Record<string, unknown>; polygon?: Record<string, unknown> }
  /**
   * Leg markers: {note,leg,label,color,precision}; fib: {levels,extend,labelMode,…};
   * trade-idea levels: {ideaId,ideaSeq,ideaColor,dir,role,slot,note,hidden}.
   */
  extendData?: Record<string, unknown>
  /** Entry→exit connector segments are locked. */
  lock?: boolean
}

/** What has been shared with the AI chat — the diff base for incremental sends. */
export interface AiPushState {
  /** When the last chart context was sent (ms), null = never. */
  sentAt: number | null
  /** Context items (key → text) included in the last send — diff base. */
  sentItems: Record<string, string> | null
}

/** Everything symbol-bound: the full chart environment for one market. */
export interface SymbolSliceState {
  timeframe: string
  chartType: ChartTypeId
  /** Last visible candle (unix seconds) + zoom, to jump back to the last location. */
  view: { toTs: number | null; barSpace: number | null }
  indicators: Array<{ name: string; settings: IndicatorSettings }>
  volume: VolumeState
  notes: WorkbenchNote[]
  /** Seeds noteIdRef so restored note ids never collide with new ones. */
  noteCounter: number
  drawings: SavedDrawing[]
  aiPush?: AiPushState
  /** When this symbol was last active (ms) — LRU ordering for slice eviction. */
  touchedAt: number
}

export interface WorkbenchSavedStateV2 {
  v: 2
  /** The symbol on screen when the state was saved — restored on mount. */
  activeSymbol: string
  /** Appearance is shared across symbols. */
  chartSettings: ChartSettings
  /** Backtest-orders overlay toggle (shared); omitted when off. */
  showBacktestOrders?: boolean
  /** Chart context the next message will carry (one per bot — it carries its
   * own symbol), so a reload keeps sending it. Omitted when nothing staged. */
  staged?: StagedChart
  bySymbol: Record<string, SymbolSliceState>
}

export interface SerializeSliceInput {
  chart: Chart
  timeframe: string
  chartType: ChartTypeId
  indicators: IndicatorInstance[]
  volume: VolumeState
  notes: WorkbenchNote[]
  noteCounter: number
  drawIds: Set<string>
  aiPush?: AiPushState
  touchedAt: number
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

/** Drop undefined entries so the JSON stays minimal and round-trips cleanly. */
function compact(obj: Record<string, unknown>): Record<string, unknown> | undefined {
  const out = Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))
  return Object.keys(out).length > 0 ? out : undefined
}

export function serializeSymbolSlice(input: SerializeSliceInput): SymbolSliceState {
  const { chart, indicators, drawIds } = input

  // Last visible candle: same index clamping the Show State dialog used.
  let toTs: number | null = null
  const vr = chart.getVisibleRange()
  const list = chart.getDataList()
  if (list.length > 0) {
    const clamp = (i: number) => Math.min(Math.max(i, 0), list.length - 1)
    const ts = list[clamp(vr.to - 1)]?.timestamp
    toTs = ts != null ? Math.floor(ts / 1000) : null
  }
  const barSpace = chart.getBarSpace()

  // Indicator companion overlays (e.g. RSI bands) are recreated by applyIndicator —
  // exclude them even if they ever leak into the drawing set.
  const bandIds = new Set(indicators.flatMap((i) => i.bandIds))

  const drawings: SavedDrawing[] = []
  drawIds.forEach((id) => {
    if (bandIds.has(id)) return
    const o = chart.getOverlayById(id)
    if (!o) return // deleted under us
    const styles = (o.styles ?? {}) as Record<string, unknown>
    const line = isRecord(styles.line) ? compact(styles.line) : undefined
    const polygon = isRecord(styles.polygon) ? compact(styles.polygon) : undefined
    const rawExtend = isRecord(o.extendData) ? o.extendData : undefined
    // Trade marks carry render state (`active` = this idea is the one being
    // edited) that the overlay can only see through extendData. It is not user
    // data, and it must not be written: persisting it restores a chart stuck in
    // edit mode, and — since refreshChartContext serializes through here too —
    // it would make merely opening an idea read as a chart change to the AI.
    const extendData =
      rawExtend && isTradeMark(o.name) ? stripTransientMarkKeys(rawExtend) : rawExtend
    drawings.push({
      name: o.name,
      points: (o.points ?? []).map((p) =>
        compact({ timestamp: p.timestamp, value: p.value }) ?? {},
      ),
      ...(line || polygon ? { styles: compact({ line, polygon }) } : {}),
      ...(extendData ? { extendData } : {}),
      ...(o.lock ? { lock: true } : {}),
    })
  })

  const ai = input.aiPush
  const aiEmpty = !ai || (ai.sentAt == null && ai.sentItems == null)

  return {
    timeframe: input.timeframe,
    chartType: input.chartType,
    view: { toTs, barSpace: isFiniteNumber(barSpace) ? barSpace : null },
    indicators: indicators.map((i) => ({ name: i.name, settings: i.settings })),
    volume: input.volume,
    notes: input.notes,
    noteCounter: input.noteCounter,
    drawings,
    ...(aiEmpty ? {} : { aiPush: ai }),
    touchedAt: input.touchedAt,
  }
}

/** Most symbols a blob keeps — the least-recently-active slice beyond this is
 * silently dropped on save (the active symbol is never evicted). */
export const MAX_SYMBOL_SLICES = 12
/** Headroom under the server's 1MB state cap (BotWorkbench MAX_STATE_CHARS). */
const SOFT_SIZE_LIMIT = 900_000

/**
 * Assemble the full persisted blob from the shared settings + slice map
 * (which must already include the active symbol's slice). Pure; evicts
 * least-recently-touched non-active slices past MAX_SYMBOL_SLICES, then keeps
 * evicting while the serialized blob would still exceed the server's cap.
 */
export function composeWorkbenchState(input: {
  activeSymbol: string
  chartSettings: ChartSettings
  showBacktestOrders?: boolean
  staged?: StagedChart | null
  bySymbol: Record<string, SymbolSliceState>
}): WorkbenchSavedStateV2 {
  const bySymbol = { ...input.bySymbol }
  const stale = Object.keys(bySymbol)
    .filter((s) => s !== input.activeSymbol)
    .sort((a, b) => bySymbol[a].touchedAt - bySymbol[b].touchedAt)
  while (Object.keys(bySymbol).length > MAX_SYMBOL_SLICES && stale.length > 0) {
    delete bySymbol[stale.shift()!]
  }
  const state: WorkbenchSavedStateV2 = {
    v: 2,
    activeSymbol: input.activeSymbol,
    chartSettings: input.chartSettings,
    // Omit-when-false keeps payloads minimal (no spurious autosave/dirty flips).
    ...(input.showBacktestOrders ? { showBacktestOrders: true } : {}),
    ...(input.staged ? { staged: input.staged } : {}),
    bySymbol,
  }
  while (stale.length > 0 && JSON.stringify(state).length > SOFT_SIZE_LIMIT) {
    delete bySymbol[stale.shift()!]
  }
  return state
}

/**
 * Does this context add anything the AI hasn't already been sent? Never sent
 * (prev = null) always counts as new — the first message shares the chart even
 * if it only names the market.
 */
export function hasUnsentItems(
  items: Record<string, string>,
  prev: Record<string, string> | null,
): boolean {
  if (!prev) return true
  const keys = Object.keys(items)
  if (keys.length !== Object.keys(prev).length) return true
  return keys.some((k) => prev[k] !== items[k])
}

const CHART_TYPE_IDS = new Set<string>(CHART_TYPES.map((t) => t.id))
const TIMEFRAMES = new Set<string>(TIMEFRAME_OPTIONS)

function parseNote(raw: unknown): WorkbenchNote | null {
  if (!isRecord(raw)) return null
  const pos = raw.pos
  if (!isRecord(pos) || !isFiniteNumber(pos.timestamp) || !isFiniteNumber(pos.value)) return null
  const kind = raw.kind === 'callout' ? 'callout' : raw.kind === 'text' ? 'text' : null
  if (!kind) return null
  const tail = raw.tail
  const validTail =
    isRecord(tail) && isFiniteNumber(tail.timestamp) && isFiniteNumber(tail.value)
      ? { timestamp: tail.timestamp, value: tail.value }
      : undefined
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : `note-restored-${pos.timestamp}`,
    kind,
    pos: { timestamp: pos.timestamp, value: pos.value },
    ...(validTail ? { tail: validTail } : {}),
    text: typeof raw.text === 'string' ? raw.text : '',
    size: isFiniteNumber(raw.size) ? raw.size : 14,
    bold: raw.bold === true,
    color: typeof raw.color === 'string' ? raw.color : '',
    bg: typeof raw.bg === 'string' ? raw.bg : '',
  }
}

function parseDrawing(raw: unknown): SavedDrawing | null {
  if (!isRecord(raw)) return null
  if (typeof raw.name !== 'string' || !raw.name) return null
  if (!Array.isArray(raw.points) || raw.points.length === 0) return null
  const points = raw.points.filter(isRecord).map((p) => ({
    ...(isFiniteNumber(p.timestamp) ? { timestamp: p.timestamp } : {}),
    ...(isFiniteNumber(p.value) ? { value: p.value } : {}),
  }))
  if (points.length === 0 || points.some((p) => p.timestamp == null && p.value == null)) return null
  const styles = isRecord(raw.styles) ? raw.styles : undefined
  return {
    name: raw.name,
    points,
    ...(styles
      ? {
          styles: {
            ...(isRecord(styles.line) ? { line: styles.line } : {}),
            ...(isRecord(styles.polygon) ? { polygon: styles.polygon } : {}),
          },
        }
      : {}),
    ...(isRecord(raw.extendData) ? { extendData: raw.extendData } : {}),
    ...(raw.lock === true ? { lock: true } : {}),
  }
}

function parseIndicator(raw: unknown): { name: string; settings: IndicatorSettings } | null {
  if (!isRecord(raw) || typeof raw.name !== 'string') return null
  if (!indicatorByName(raw.name)) return null // unknown to this build — drop
  const s = isRecord(raw.settings) ? raw.settings : {}
  return {
    name: raw.name,
    settings: {
      params: Array.isArray(s.params) ? s.params.filter(isFiniteNumber) : [],
      colors: Array.isArray(s.colors) ? s.colors.filter((c): c is string => typeof c === 'string') : [],
      thickness: isFiniteNumber(s.thickness) ? s.thickness : 1,
      ...(typeof s.textColor === 'string' ? { textColor: s.textColor } : {}),
      visible: s.visible !== false,
      lastValueMark: s.lastValueMark === true,
      valuesInLegend: s.valuesInLegend !== false,
    },
  }
}

/** Corrupt staged context degrades to undefined — never rejects the state. */
function parseStaged(raw: unknown): StagedChart | undefined {
  if (!isRecord(raw)) return undefined
  if (typeof raw.label !== 'string' || typeof raw.text !== 'string') return undefined
  if (typeof raw.symbol !== 'string' || raw.symbol.length === 0) return undefined
  const items = isRecord(raw.items)
    ? Object.fromEntries(Object.entries(raw.items).filter(([, v]) => typeof v === 'string'))
    : {}
  return {
    label: raw.label,
    text: raw.text,
    items: items as Record<string, string>,
    symbol: raw.symbol,
    // Blobs written before the tick box carried atLabel/changes instead; the
    // context is still valid, it just has no build time to show.
    atMs: isFiniteNumber(raw.atMs) && raw.atMs > 0 ? raw.atMs : 0,
  }
}

/** Corrupt aiPush degrades to undefined — never rejects the whole slice. */
function parseAiPush(raw: unknown): AiPushState | undefined {
  if (!isRecord(raw)) return undefined
  const sentItems = isRecord(raw.sentItems)
    ? Object.fromEntries(Object.entries(raw.sentItems).filter(([, v]) => typeof v === 'string'))
    : null
  return {
    sentAt: isFiniteNumber(raw.sentAt) ? raw.sentAt : null,
    sentItems: sentItems && Object.keys(sentItems).length > 0 ? (sentItems as Record<string, string>) : null,
  }
}

/**
 * Validate one symbol slice, or null to drop just that slice. Also accepts a
 * v1 blob's top level — the v1 flat fields are a superset of the slice shape,
 * which is what makes the v1→v2 migration a one-liner.
 */
function parseSymbolSlice(raw: unknown): SymbolSliceState | null {
  if (!isRecord(raw)) return null
  if (typeof raw.timeframe !== 'string' || !TIMEFRAMES.has(raw.timeframe)) return null
  if (typeof raw.chartType !== 'string' || !CHART_TYPE_IDS.has(raw.chartType)) return null

  const view = isRecord(raw.view) ? raw.view : {}
  const volume = isRecord(raw.volume) ? raw.volume : {}
  const notes = Array.isArray(raw.notes)
    ? raw.notes.map(parseNote).filter((n): n is WorkbenchNote => n !== null)
    : []

  return {
    timeframe: raw.timeframe,
    chartType: raw.chartType as ChartTypeId,
    view: {
      toTs: isFiniteNumber(view.toTs) ? view.toTs : null,
      barSpace: isFiniteNumber(view.barSpace) ? view.barSpace : null,
    },
    indicators: Array.isArray(raw.indicators)
      ? raw.indicators
          .map(parseIndicator)
          .filter((i): i is { name: string; settings: IndicatorSettings } => i !== null)
      : [],
    volume: {
      present: volume.present !== false,
      visible: volume.visible !== false,
      up: typeof volume.up === 'string' ? volume.up : null,
      down: typeof volume.down === 'string' ? volume.down : null,
    },
    notes,
    noteCounter: isFiniteNumber(raw.noteCounter) ? raw.noteCounter : notes.length,
    drawings: Array.isArray(raw.drawings)
      ? raw.drawings.map(parseDrawing).filter((d): d is SavedDrawing => d !== null)
      : [],
    ...(raw.aiPush !== undefined ? { aiPush: parseAiPush(raw.aiPush) } : {}),
    // v1 blobs carry no touchedAt (0 = oldest); harmless — the migrated slice
    // becomes the active one, which is re-stamped live and never evicted.
    touchedAt: isFiniteNumber(raw.touchedAt) ? raw.touchedAt : 0,
  }
}

/**
 * Validate an untrusted saved blob into a usable state, or null to fall back
 * to defaults silently (unknown version, corrupt shape). A v1 blob is
 * migrated in memory: its single symbol becomes the one bySymbol entry. In a
 * v2 blob a corrupt slice is dropped individually — the rest survives.
 */
export function parseWorkbenchState(raw: unknown): WorkbenchSavedStateV2 | null {
  if (!isRecord(raw)) return null

  if (raw.v === 1) {
    if (typeof raw.symbol !== 'string' || raw.symbol.length === 0) return null
    const slice = parseSymbolSlice(raw)
    if (!slice) return null
    return {
      v: 2,
      activeSymbol: raw.symbol,
      chartSettings: isRecord(raw.chartSettings) ? (raw.chartSettings as ChartSettings) : {},
      ...(raw.showBacktestOrders === true ? { showBacktestOrders: true } : {}),
      bySymbol: { [raw.symbol]: slice },
    }
  }

  if (raw.v !== 2) return null
  if (typeof raw.activeSymbol !== 'string' || raw.activeSymbol.length === 0) return null
  if (!isRecord(raw.bySymbol)) return null

  const bySymbol: Record<string, SymbolSliceState> = {}
  for (const [sym, entry] of Object.entries(raw.bySymbol)) {
    if (!sym) continue
    const slice = parseSymbolSlice(entry)
    if (slice) bySymbol[sym] = slice
  }
  if (Object.keys(bySymbol).length === 0) return null

  const staged = raw.staged !== undefined ? parseStaged(raw.staged) : undefined
  return {
    v: 2,
    activeSymbol: raw.activeSymbol,
    chartSettings: isRecord(raw.chartSettings) ? (raw.chartSettings as ChartSettings) : {},
    ...(raw.showBacktestOrders === true ? { showBacktestOrders: true } : {}),
    ...(staged ? { staged } : {}),
    bySymbol,
  }
}
